import { useCallback, useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import useWS from '../components/schedule/hooks/useWS';
import { MeContext } from '../contexts/me.context';
import messagingService from '../services/messaging.service';
import { UnreadStatusResponse } from '../types/messaging.types';

// standalone hook to mark messages as read without subscribing to WebSocket
export const useMarkMessagesAsRead = () => {
  const { me, selectedLocationFull } = useContext(MeContext);
  const clinicId = me?.selectedClinic?.ID;
  const locationId = selectedLocationFull?.ID as number | undefined;
  const queryClient = useQueryClient();

  return useCallback(() => {
    if (!clinicId || !locationId) return;
    queryClient.setQueryData<UnreadStatusResponse | undefined>(
      ['unreadMessages', clinicId, locationId],
      old => ({
        hasUnread: false,
        latestMessageTimestamp: old?.latestMessageTimestamp,
        unreadCount: 0,
      })
    );
  }, [clinicId, locationId, queryClient]);
};

export const useUnreadMessages = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { me, selectedLocationFull } = useContext(MeContext);
  const clinicId = me?.selectedClinic?.ID;
  const locationId = selectedLocationFull?.ID as number | undefined;
  const location = useLocation();
  const queryClient = useQueryClient();

  const isOnMessagingPage = location.pathname.startsWith('/messaging');

  // Initial fetch to get the current unread status (one-time only)
  const { data } = useQuery<UnreadStatusResponse>(
    ['unreadMessages', clinicId, locationId],
    () => messagingService.getUnreadStatus(clinicId || -1, undefined, locationId),
    {
      enabled: enabled && !!clinicId && !!locationId,
      // No polling - we rely on WebSocket for updates
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  // Handle WebSocket messages for unread status updates
  const handleWebSocketMessage = useCallback(
    (wsData: any) => {
      if (!clinicId || !locationId) return;

      const isMessagingEvent =
        wsData.type === 'newMessage' || wsData.type === 'createMessage' || Boolean(wsData.messages);

      // Process all inbound messages regardless of page location
      // The badge should update even if user is on messaging page (different conversation)
      // An AI Receptionist session ended: the server just marked that
      // conversation unread for staff review — refetch the real count
      // rather than guessing locally.
      if (wsData.type === 'aiSessionEnded') {
        queryClient.invalidateQueries(['unreadMessages', clinicId, locationId]);

        return;
      }

      if (isMessagingEvent) {
        // Only increment for inbound messages (from patient)
        const isInboundMessage =
          wsData.data?.direction === 'inbound' || wsData.direction === 'inbound';

        // While the AI Receptionist is handling the conversation the message
        // is already marked read server-side — no badge nagging; the
        // conversation surfaces as unread when the AI session ends.
        const isAiHandled = wsData.data?.aiActive === true;

        if (isInboundMessage && !isAiHandled) {
          // Refetch the server count instead of incrementing locally
          queryClient.invalidateQueries(['unreadMessages', clinicId, locationId]);
        }
      }
    },
    [clinicId, locationId, queryClient]
  );

  // Subscribe to WebSocket updates for unread messages
  useWS(
    enabled && clinicId && locationId ? `messaging:${clinicId}:${locationId}` : null,
    handleWebSocketMessage
  );

  const markAsRead = useCallback(() => {
    if (!clinicId || !locationId) return;
    queryClient.setQueryData<UnreadStatusResponse | undefined>(
      ['unreadMessages', clinicId, locationId],
      old => ({
        hasUnread: false,
        latestMessageTimestamp: old?.latestMessageTimestamp,
        unreadCount: 0,
      })
    );
  }, [clinicId, locationId, queryClient]);

  // Mark as read when navigating to messaging page
  useEffect(() => {
    if (isOnMessagingPage) {
      markAsRead();
    }
  }, [isOnMessagingPage, markAsRead]);

  // when on messaging route, always show 0 unread (aka, hide 'unread messages badge')
  // on any other route, show actual unread count
  const actualUnreadCount = !enabled || isOnMessagingPage ? 0 : (data?.unreadCount ?? 0);

  return {
    hasUnreadMessages: actualUnreadCount > 0,
    unreadCount: actualUnreadCount,
    markAsRead,
  };
};
